import { ImageFullscreen } from "@/components/modal/imageFullscreen";
import {
  CATALOG_THUMBNAIL_BOX_CLASS,
  CATALOG_THUMBNAIL_IMAGE_CLASS,
} from "@/pages/dashboard/shared/catalogMediaThumbnail";
import { CircleMinus, CirclePlus } from "lucide-react";
import { SupportedLocale } from "@/i18n";

const cardTextClass =
  "text-sm leading-[110%] subpixel-antialiased font-sans [&_*]:!text-[14px] [&_*]:!leading-[110%]";

interface RelatedItemCardProps {
  item: any;
  type:
    | "artwork"
    | "document"
    | "multipleprint"
    | "photography"
    | "writing"
    | "audiovisual";
  locale: SupportedLocale;
  expandedDocs?: Record<string, boolean>;
  onToggleExpand?: (id: string) => void;
  t: (key: string) => string;
}

export function RelatedItemCard({
  item,
  type,
  expandedDocs,
  onToggleExpand,
  t,
}: RelatedItemCardProps) {
  const getImageAlt = () => {
    switch (type) {
      case "artwork":
        return item.oeuvresDescriptionTitre || "Artwork image";
      case "document":
        return item.documentsDescriptionTitre || "Document image";
      case "multipleprint":
        return item.estampesMultiplesDescriptionTitre || "Multipleprint image";
      case "photography":
        return item.photosDescriptionLegendeRedigee || "Photography image";
      case "writing":
        return item.documentsDescriptionTitre || "Writing image";
      case "audiovisual":
        return item.audiovisuelDescriptionResume || "Audiovisual image";
      default:
        return "Image";
    }
  };

  const getContent = () => {
    switch (type) {
      case "artwork":
        return item.oeuvresDescriptionResume;
      case "document":
        return item.documentsDescriptionLegendeRedigee;
      case "multipleprint":
        return item.estampesMultiplesDescriptionResume;
      case "photography":
        return item.photosDescriptionLegendeRedigee;
      case "writing":
        return item.ecritsDescriptionLegendeRedigee;
      case "audiovisual":
        return item.audiovisuelDescriptionResume;
      default:
        return "";
    }
  };

  const hasLongContent =
    type === "document" && (getContent()?.length ?? 0) > 200;
  const isExpanded = expandedDocs?.[item.id];

  return (
    <div className="py-1">
      <div
        className={`flex items-start gap-4 ${type === "photography" ? "" : "w-full"}`}
      >
        <div className={CATALOG_THUMBNAIL_BOX_CLASS}>
          {item.attachments && item.attachments.length > 0 ? (
            <ImageFullscreen
              catalogFullscreen
              media={{
                attachments: item.attachments,
                type: type === "artwork" ? "artwork" : type,
                alt: getImageAlt(),
                width: 100,
                height: 100,
                className: CATALOG_THUMBNAIL_IMAGE_CLASS,
              }}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center rounded bg-gray-200">
              <span className="text-xs text-gray-400">{t("noImage")}</span>
            </div>
          )}
        </div>
        <div
          className={
            type === "document" ||
            type === "photography" ||
            type === "writing" ||
            type === "audiovisual"
              ? "flex-1"
              : ""
          }
        >
          {hasLongContent && onToggleExpand ? (
            <div className="flex flex-col gap-1">
              <div
                className={`${cardTextClass} ${isExpanded ? "" : "line-clamp-2"}`}
                dangerouslySetInnerHTML={{
                  __html: getContent() ?? "",
                }}
              />
              <button
                type="button"
                onClick={() => onToggleExpand(item.id)}
                className="mt-1 flex items-center gap-1 text-sm text-muted-foreground hover:underline"
              >
                {isExpanded ? (
                  <CircleMinus size={14} />
                ) : (
                  <CirclePlus size={14} />
                )}
              </button>
            </div>
          ) : (
            <div
              className={`${cardTextClass} ${type === "document" ? "line-clamp-2" : ""}`}
              dangerouslySetInnerHTML={{
                __html: getContent() ?? "",
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
